import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import {
  analyticsApiService, type AnalyticsOverview, type RecentView, type TemplateAnalyticsRow,
} from '../services/analyticsApiService'
import { ApiError } from '../services/BaseApiService'
import { colors, fontFamily, fontSize, spacing } from '../styles/theme'
import {
  capitalize, delta, fmtDateTime, fmtHours, fmtNum, fmtPct, formatLabel, ratio, surfaceLabel,
} from '../utils/analyticsFormat'
import {
  BarList, card, DailyChart, EmptyState, ErrorNote, gridAuto, KpiCard, numTd, RangePicker, SectionTitle, TabStrip,
  TableWrap, td, th, VIEWS_EMPTY_TEXT,
} from '../components/analytics/AnalyticsUi'
import DocumentViewsDrawer from '../components/analytics/DocumentViewsDrawer'
import PerformancePanel from '../components/analytics/PerformancePanel'

type Tab = 'overview' | 'templates' | 'engagement' | 'performance'
const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'templates', label: 'Templates' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'performance', label: 'Performance' },
]

const templateLink = (id: string) => `/app/templates/${id}?tab=analytics`
const loadError = (e: unknown, what: string) => (e instanceof ApiError ? e.message : `Failed to load ${what}`)

function useOverview(days: number) {
  const [data, setData] = useState<AnalyticsOverview | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let live = true
    setLoading(true); setError('')
    analyticsApiService.getOverview(days)
      .then(d => { if (live) setData(d) })
      .catch(e => { if (live) setError(loadError(e, 'analytics')) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [days])
  return { data, error, loading }
}

function OverviewTab({ days }: { days: number }) {
  const navigate = useNavigate()
  const { data, error, loading } = useOverview(days)
  if (error) return <ErrorNote message={error} />
  if (loading && !data) return <div style={{ color: colors.textSecondary }}>Loading…</div>
  if (!data) return null
  const { totals: t, previous: p } = data
  const noViews = t.views === 0

  return (
    <div>
      <div style={{ ...gridAuto(150), marginBottom: spacing.lg }}>
        <KpiCard label="Documents generated" value={fmtNum(t.documents)} change={delta(t.documents, p.documents)} />
        <KpiCard label="Pages" value={fmtNum(t.pages)} change={delta(t.pages, p.pages)} />
        <KpiCard label="Success rate" value={fmtPct(t.successRate)} hint={t.failed ? `${fmtNum(t.failed)} failed` : undefined}
          tone={t.successRate != null && t.successRate < 0.95 ? colors.warning : undefined} />
        <KpiCard label="Views" value={fmtNum(t.views)} change={delta(t.views, p.views)} hint={`${fmtNum(t.downloads)} downloads`} />
        <KpiCard label="Unique viewers" value={fmtNum(t.uniqueViewers)} change={delta(t.uniqueViewers, p.uniqueViewers)} />
        <KpiCard label="Viewed documents" value={fmtPct(ratio(t.viewedDocuments, t.documents))} hint={`${fmtNum(t.viewedDocuments)} of ${fmtNum(t.documents)}`} />
        <KpiCard label="Avg time to first view" value={fmtHours(t.avgFirstViewHours)} />
      </div>

      <div style={{ ...card, marginBottom: spacing.lg }}>
        <SectionTitle>Documents vs views</SectionTitle>
        {t.documents === 0 && t.views === 0 ? (
          <EmptyState title="No activity yet">Render a template to start generating documents. {VIEWS_EMPTY_TEXT}</EmptyState>
        ) : (
          <DailyChart rows={data.daily} series={[
            { key: 'documents', label: 'Documents', color: colors.primary },
            { key: 'views', label: 'Views', color: colors.info },
          ]} />
        )}
      </div>

      <div style={{ ...gridAuto(260), marginBottom: spacing.lg }}>
        <div style={card}>
          <SectionTitle>By output format</SectionTitle>
          <BarList items={data.byFormat.map(f => ({ label: formatLabel(f.format), value: f.documents, sub: `${fmtNum(f.pages)} pages` }))}
            emptyText="No documents generated in this period." />
        </div>
        <div style={card}>
          <SectionTitle>Views by surface</SectionTitle>
          {noViews ? <EmptyState title="No views yet">{VIEWS_EMPTY_TEXT}</EmptyState> : (
            <BarList color={colors.info} items={data.bySurface.map(s => ({ label: surfaceLabel(s.surface), value: s.views, sub: `${fmtNum(s.uniqueViewers)} unique` }))} />
          )}
        </div>
        <div style={card}>
          <SectionTitle>Device mix</SectionTitle>
          {noViews ? <EmptyState title="No views yet">{VIEWS_EMPTY_TEXT}</EmptyState> : (
            <BarList color={colors.info} items={data.devices.map(d => ({ label: capitalize(d.device), value: d.views, sub: fmtPct(ratio(d.views, t.views)) }))} />
          )}
        </div>
        <div style={card}>
          <SectionTitle>Top browsers</SectionTitle>
          {noViews ? <EmptyState title="No views yet">{VIEWS_EMPTY_TEXT}</EmptyState> : (
            <BarList color={colors.info} items={data.browsers.map(b => ({ label: b.browser || 'Unknown', value: b.views }))} />
          )}
        </div>
        <div style={card}>
          <SectionTitle>Top referrers</SectionTitle>
          {noViews ? <EmptyState title="No views yet">{VIEWS_EMPTY_TEXT}</EmptyState> : (
            <BarList color={colors.info} items={data.referrers.map(r => ({ label: r.domain || 'Direct', value: r.views }))} emptyText="No referrers — views came directly." />
          )}
        </div>
      </div>

      <SectionTitle>Top templates</SectionTitle>
      {data.topTemplates.length === 0 ? (
        <div style={card}><EmptyState title="No templates used in this period">Render a template to see it here.</EmptyState></div>
      ) : (
        <TableWrap>
          <thead>
            <tr>
              <th style={th}>Template</th>
              <th style={{ ...th, textAlign: 'right' }}>Documents</th>
              <th style={{ ...th, textAlign: 'right' }}>Pages</th>
              <th style={{ ...th, textAlign: 'right' }}>Views</th>
              <th style={{ ...th, textAlign: 'right' }}>Unique viewers</th>
              <th style={{ ...th, textAlign: 'right' }}>Failed</th>
            </tr>
          </thead>
          <tbody>
            {data.topTemplates.map(r => (
              <tr key={r.templateId} onClick={() => navigate(templateLink(r.templateId))} style={{ cursor: 'pointer' }}>
                <td style={{ ...td, color: colors.primary, fontWeight: 500 }}>{r.name}</td>
                <td style={numTd}>{fmtNum(r.documents)}</td>
                <td style={numTd}>{fmtNum(r.pages)}</td>
                <td style={numTd}>{fmtNum(r.views)}</td>
                <td style={numTd}>{fmtNum(r.uniqueViewers)}</td>
                <td style={{ ...numTd, color: r.failed ? colors.error : colors.textSecondary }}>{fmtNum(r.failed)}</td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </div>
  )
}

type SortKey = 'name' | 'documents' | 'pages' | 'failedRate' | 'views' | 'uniqueViewers' | 'lastRenderedAt' | 'lastViewedAt'
const COLUMNS: { key: SortKey; label: string; numeric?: boolean }[] = [
  { key: 'name', label: 'Template' },
  { key: 'documents', label: 'Documents', numeric: true },
  { key: 'pages', label: 'Pages', numeric: true },
  { key: 'failedRate', label: 'Failed %', numeric: true },
  { key: 'views', label: 'Views', numeric: true },
  { key: 'uniqueViewers', label: 'Unique viewers', numeric: true },
  { key: 'lastRenderedAt', label: 'Last rendered' },
  { key: 'lastViewedAt', label: 'Last viewed' },
]

function sortValue(r: TemplateAnalyticsRow, k: SortKey): string | number {
  switch (k) {
    case 'name': return r.name.toLowerCase()
    case 'failedRate': return r.documents ? r.failed / r.documents : -1
    case 'lastRenderedAt': return r.lastRenderedAt ? Date.parse(r.lastRenderedAt) : 0
    case 'lastViewedAt': return r.lastViewedAt ? Date.parse(r.lastViewedAt) : 0
    default: return r[k]
  }
}

function TemplatesTab({ days }: { days: number }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState<TemplateAnalyticsRow[] | null>(null)
  const [error, setError] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'documents', dir: -1 })

  useEffect(() => {
    let live = true
    setError('')
    analyticsApiService.getTemplates(days)
      .then(d => { if (live) setRows(d) })
      .catch(e => { if (live) setError(loadError(e, 'template analytics')) })
    return () => { live = false }
  }, [days])

  const sorted = useMemo(() => {
    if (!rows) return []
    return [...rows].sort((a, b) => {
      const x = sortValue(a, sort.key), y = sortValue(b, sort.key)
      return (x < y ? -1 : x > y ? 1 : 0) * sort.dir
    })
  }, [rows, sort])

  if (error) return <ErrorNote message={error} />
  if (!rows) return <div style={{ color: colors.textSecondary }}>Loading…</div>
  if (rows.length === 0) {
    return <div style={card}><EmptyState title="No template activity in this period">Render a template to see its documents and views here.</EmptyState></div>
  }

  const toggle = (key: SortKey) =>
    setSort(s => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: key === 'name' ? 1 : -1 }))

  return (
    <TableWrap>
      <thead>
        <tr>
          {COLUMNS.map(c => (
            <th key={c.key} style={{ ...th, textAlign: c.numeric ? 'right' : 'left', padding: 0 }}
              aria-sort={sort.key === c.key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
              <button type="button" onClick={() => toggle(c.key)} style={{
                all: 'unset', cursor: 'pointer', display: 'block', width: '100%', boxSizing: 'border-box',
                padding: `${spacing.sm} ${spacing.md}`, textAlign: c.numeric ? 'right' : 'left',
              }}>
                {c.label}{sort.key === c.key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
              </button>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map(r => (
          <tr key={r.templateId} data-testid="template-row" onClick={() => navigate(templateLink(r.templateId))} style={{ cursor: 'pointer' }}>
            <td style={{ ...td, color: colors.primary, fontWeight: 500 }}>{r.name}</td>
            <td style={numTd}>{fmtNum(r.documents)}</td>
            <td style={numTd}>{fmtNum(r.pages)}</td>
            <td style={{ ...numTd, color: r.failed ? colors.error : colors.textSecondary }}>{fmtPct(ratio(r.failed, r.documents))}</td>
            <td style={numTd}>{fmtNum(r.views)}</td>
            <td style={numTd}>{fmtNum(r.uniqueViewers)}</td>
            <td style={{ ...td, color: colors.textSecondary }}>{fmtDateTime(r.lastRenderedAt)}</td>
            <td style={{ ...td, color: colors.textSecondary }}>{fmtDateTime(r.lastViewedAt)}</td>
          </tr>
        ))}
      </tbody>
    </TableWrap>
  )
}

function EngagementTab({ days }: { days: number }) {
  const { data, error } = useOverview(days)
  const [recent, setRecent] = useState<RecentView[] | null>(null)
  const [recentError, setRecentError] = useState('')
  const [openJob, setOpenJob] = useState<string | null>(null)

  useEffect(() => {
    analyticsApiService.getRecentViews(50)
      .then(setRecent)
      .catch(e => setRecentError(loadError(e, 'recent views')))
  }, [])

  const surfaces = (['share', 'preview', 'esign', 'embed'] as const).map(s =>
    data?.bySurface.find(x => x.surface === s) ?? { surface: s, views: 0, downloads: 0, uniqueViewers: 0 })

  return (
    <div>
      {error && <ErrorNote message={error} />}
      <div style={{ ...gridAuto(180), marginBottom: spacing.lg }}>
        {surfaces.map(s => (
          <div key={s.surface} style={card} data-testid={`surface-${s.surface}`}>
            <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.xs, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {surfaceLabel(s.surface)}
            </div>
            <div style={{ fontSize: fontSize.xl, fontWeight: 700, color: colors.text }}>{data ? fmtNum(s.views) : '…'} <span style={{ fontSize: fontSize.sm, fontWeight: 400, color: colors.textSecondary }}>views</span></div>
            <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>
              {fmtNum(s.downloads)} downloads · {fmtNum(s.uniqueViewers)} unique viewers
            </div>
          </div>
        ))}
      </div>

      <div style={card}>
        <SectionTitle>Recent views</SectionTitle>
        {recentError && <ErrorNote message={recentError} />}
        {!recent && !recentError && <div style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Loading…</div>}
        {recent && recent.length === 0 && <EmptyState title="No views yet">{VIEWS_EMPTY_TEXT}</EmptyState>}
        {recent && recent.length > 0 && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {recent.map((v, i) => (
              <li key={`${v.renderJobId}-${v.occurredAt}-${i}`} style={{ borderBottom: i < recent.length - 1 ? `1px solid ${colors.border}` : 'none' }}>
                <button
                  type="button"
                  data-testid="recent-view"
                  onClick={() => setOpenJob(v.renderJobId)}
                  style={{
                    all: 'unset', cursor: 'pointer', display: 'flex', flexWrap: 'wrap', gap: `2px ${spacing.md}`,
                    width: '100%', boxSizing: 'border-box', padding: `${spacing.sm} 0`, alignItems: 'baseline',
                  }}
                >
                  <span style={{ fontSize: fontSize.sm, color: colors.text, fontWeight: 500, flex: '1 1 200px', minWidth: 0 }}>
                    {v.event === 'download' ? 'Downloaded' : 'Viewed'} · {v.templateName ?? 'Document'}{' '}
                    <span style={{ fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.textMuted }}>{v.renderJobId.slice(0, 8)}</span>
                  </span>
                  <span style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                    {surfaceLabel(v.surface)} · {[capitalize(v.device), v.browser].filter(Boolean).join(' / ')}
                    {v.visitor && <> · <span style={{ fontFamily: fontFamily.mono }}>{v.visitor}</span></>}
                  </span>
                  <span style={{ fontSize: fontSize.xs, color: colors.textMuted, whiteSpace: 'nowrap' }}>{fmtDateTime(v.occurredAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {openJob && <DocumentViewsDrawer renderJobId={openJob} onClose={() => setOpenJob(null)} />}
    </div>
  )
}

export default function AnalyticsPage() {
  const [params, setParams] = useSearchParams()
  const tabParam = params.get('tab') as Tab | null
  const tab: Tab = TABS.some(t => t.id === tabParam) ? tabParam! : 'overview'
  const [days, setDays] = useState(30)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, marginBottom: spacing.lg, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, letterSpacing: '-0.02em', margin: 0, flex: 1 }}>
          Analytics
        </h1>
        <RangePicker days={days} onChange={setDays} />
      </div>
      <TabStrip tabs={TABS} active={tab} onChange={id => setParams(id === 'overview' ? {} : { tab: id }, { replace: true })} />
      {tab === 'overview' && <OverviewTab days={days} />}
      {tab === 'templates' && <TemplatesTab days={days} />}
      {tab === 'engagement' && <EngagementTab days={days} />}
      {tab === 'performance' && <PerformancePanel days={days} />}
    </div>
  )
}
